import Switch from '@mui/material/Switch';
import EditIcon from '@mui/icons-material/ModeEdit';
import { Link, useNavigate } from 'react-router-dom';
import BarChartIcon from '@mui/icons-material/StackedBarChart';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShowChart from '@mui/icons-material/ShowChart';
import * as React from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TV from '../../services/interfaces/tv-interface';
import { isLoggedIn } from '../../services/isLoggedInService';
import ManageLivetvService from '../../services/promote-to-trends-tvs';
import { updateVisibility } from '../../services/visibilityPublishService';
import { RingLoader } from 'react-spinners';
import { AxiosInstance } from '../../common/Loader/axios.config';
import EventService from '../../services/event-service';

const ITEM_HEIGHT = 48;

const TVs: React.FC<{ loggedInProvider: { id: string; userType: string } }> = ({
  loggedInProvider,
}) => {
  const [tvs, setTVs] = React.useState<TV[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [sortBy, setSortBy] = React.useState<'views' | 'comments' | 'name'>('views');
  const [filterName, setFilterName] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [contentViews, setContentViews] = React.useState<any[]>([]);

  const open = Boolean(anchorEl);
  const category = 'tvs';
  const manageLivetvService = new ManageLivetvService();
  const navigate = useNavigate();

  // Auth guard
  React.useEffect(() => {
    if (!isLoggedIn()) navigate('/auth/signin');
  }, [navigate]);

  // Fetch TVs (new schema)
  React.useEffect(() => {
    const fetchTVs = async () => {
      try {
        setLoading(true);
        const fetched = await EventService.getNewSchemaEvents();

        const normalized = fetched
          .map((tv: any) => ({
            ...tv,
            status: {
              type: tv.status?.type || 'normal',
              lifetime: tv.status?.lifetime ?? 0,
              visibility: Boolean(tv.status?.visibility),
              publish: tv.status?.publish === 'true' || tv.status?.publish === true,
              promoted: Boolean(tv.status?.promoted),
              live: Boolean(tv.status?.live),
            },
            video_list: Array.isArray(tv.video_list) ? tv.video_list : [],
            providerObj: Array.isArray(tv.providerObj) ? tv.providerObj : [],
            projectObj: Array.isArray(tv.projectObj) ? tv.projectObj : [],
            productObj: Array.isArray(tv.productObj) ? tv.productObj : [],
          }))
          .filter((tv: any) => tv.category === 'tv');

        setTVs(normalized);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching TVs:', err);
        setLoading(false);
      }
    };

    fetchTVs();
  }, []);

  // Toggle visibility / publish (same pattern as events)
  const handleToggleChange = async (
    tvId: string,
    field: 'visibility' | 'publish',
    newValue: boolean
  ) => {
    try {
      const currentTV = tvs.find((tv) => tv._id === tvId);
      if (!currentTV) return;

      const payload = {
        visibility:
          field === 'visibility' ? newValue : currentTV.status.visibility,
        publish: field === 'publish' ? newValue : currentTV.status.publish,
      };

      await updateVisibility(tvId, category, payload);

      setTVs((prev) =>
        prev.map((tv) =>
          tv._id === tvId
            ? { ...tv, status: { ...tv.status, [field]: newValue } }
            : tv
        )
      );
    } catch (err) {
      console.error('Error updating TV status:', err);
    }
  };

  // Promote
  const handlePromoteToTrendingClick = async (tvId: string) => {
    try {
      await manageLivetvService.promoteToTrending(tvId);
    } catch (err) {
      console.error(err);
    }
  };

  // Viewership
  React.useEffect(() => {
    const getViewership = async () => {
      try {
        const res = await AxiosInstance.get('users/viewership');
        if (res.status === 200) setContentViews(res.data.message || []);
      } catch (err) {
        console.error(err);
      }
    };
    getViewership();
  }, []);

  // Filters
  const filteredTVs = tvs
    .filter(
      (tv) =>
        loggedInProvider.userType === 'admin' ||
        tv.video_provider === loggedInProvider.id
    )
    .filter((tv) =>
      tv.title?.toLowerCase().includes(filterName.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'views') return (b.views || 0) - (a.views || 0);
      if (sortBy === 'comments') return (b.comments || 0) - (a.comments || 0);
      return (a.title || '').localeCompare(b.title || '');
    });

  return (
    <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-transparent dark:bg-transparent w-[1150px]">
      {/* HEADER */}
      <div className="m-4 mt-2 flex justify-between px-4">
        <div className="flex items-center">
          <p className="mr-2 text-sm text-gray-500">Sort by:</p>
          <select
            className="rounded-md border px-2 py-1 text-sm"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
          >
            <option value="views">Views</option>
            <option value="comments">Comments</option>
            <option value="name">Name</option>
          </select>
        </div>

        <div className="flex items-center">
          <p className="mr-2 text-sm text-gray-500">Filter by name:</p>
          <input
            className="rounded-full border px-3 py-1"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
          />
        </div>
      </div>

      {/* TABLE HEADER */}
      <div className="grid grid-cols-12 border-t border-stroke px-4 py-4.5">
        <div className="col-span-5 mx-12 font-medium">TV Name</div>
        <div className="col-span-1 hidden sm:flex justify-center font-medium">
          Clicks
        </div>
        <div className="col-span-1 flex justify-center font-medium">Views</div>
        <div className="col-span-1 flex justify-center font-medium">
          Comments
        </div>
        <div className="col-span-1 flex justify-center font-medium">
          Visibility
        </div>
        <div className="col-span-1 flex justify-center font-medium">
          Publish
        </div>
        <div className="col-span-1 flex justify-center font-medium">Manage</div>
      </div>

      {/* ROWS */}
      <div className="m-4 mt-2 h-screen overflow-y-scroll">
        {loading ? (
          <RingLoader color="#36D7B7" size={100} />
        ) : (
          filteredTVs.map((tv) => (
            <div
              key={tv._id}
              className="grid grid-cols-12 border-t border-stroke px-4 py-4.5"
            >
              <div className="col-span-5 flex items-center gap-4">
                <img
                  src={tv.thumb_nail}
                  alt={tv.title}
                  className="h-20 w-20 rounded-md"
                />
                <p>{tv.title}</p>
              </div>

              <div className="col-span-1 hidden sm:flex justify-center">
                {tv.video_list?.length || 0}
              </div>

              <div className="col-span-1 flex justify-center">
                {
                  contentViews.filter(
                    (v) => v.contentid === tv._id
                  ).length
                }
              </div>

              <div className="col-span-1 flex justify-center">
                {tv.comments || 0}
              </div>

              <div className="col-span-1 flex justify-center">
                <Switch
                  color="success"
                  checked={tv.status.visibility}
                  onChange={() =>
                    handleToggleChange(
                      tv._id,
                      'visibility',
                      !tv.status.visibility
                    )
                  }
                />
              </div>

              <div className="col-span-1 flex justify-center">
                <Switch
                  color="success"
                  checked={tv.status.publish}
                  onChange={() =>
                    handleToggleChange(
                      tv._id,
                      'publish',
                      !tv.status.publish
                    )
                  }
                />
              </div>

              <div className="col-span-1 flex items-center gap-2">
                <Link to={`/ManageVideoEdit/${tv._id}?category=${category}`}>
                  <EditIcon fontSize="small" />
                </Link>
                <Link to={`/ManageVideoEdit/${tv._id}?category=${category}`}>
                  <BarChartIcon fontSize="small" />
                </Link>
                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={() => setAnchorEl(null)}
                  PaperProps={{
                    style: { maxHeight: ITEM_HEIGHT * 4.5, width: '20ch' },
                  }}
                >
                  <MenuItem
                    onClick={() => handlePromoteToTrendingClick(tv.program_id)}
                  >
                    <ShowChart /> Promote to trending
                  </MenuItem>
                  <MenuItem>
                    <DeleteIcon /> Delete forever
                  </MenuItem>
                </Menu>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TVs;
